// CosmeticCard — 코스메틱 카드 렌더 (등급 이펙트 런타임 시스템, 스펙 §16-2·§16-3)
//
// 홀로·프리즘은 이미지에 굽지 않고 런타임 CSS 레이어로 입힌다 (4차 개정 ⑬).
//   N/R = 정적 프레임 · E = 은은한 홀로(무지개+글리터) ·
//   L = 프리즘: V1 다이아(기본) / V6 풀포일(+10 승격) / V3 갤럭시(시즌 한정)
// 시드 하이브리드(⑮): 스타일은 등급/티어 고정, 결(각도·색위상·패싯 크기)은 카드 UUID 해시로 유일.
// 가독성 3원칙(§16-2): 중앙 마스크 + 캐릭터 흰 후광 + 명판/플레이버 z 최상위.
// ⚠ 이펙트 스펙트럼 색 = cs-tokens 예외(§16-2 — 무지개는 본질상 풀스펙트럼).
// 데모 원본: mockups/sotda-cosmetic/holo-prism-demo.html · prism-variants.html
// 레이어 선택·시드·파티클 = cardFxEngine.ts(순수 엔진)이 산출 — 이 파일은 스펙 소비(렌더)만.
// 시그니처 벚꽃(록이 L, roki-petal-card.html 확정값)은 엔진 SIGNATURE_FX 레지스트리에서 자동 적용.
import { useRef, useCallback } from 'react';
import { CastAvatar, CAST } from './cast';
import { GRADE_META } from '@core/sim/cosmetic/cosmeticCatalog';
import { resolveCardFx, cardSeed } from '../sim/cardFxEngine';

/* 하위호환 re-export — 카드 UUID → 결정적 시드(렌더 시 산출, DB 추가 0) */
export { cardSeed };

const CSS = `
.cosc { position:relative; overflow:hidden; border-radius:11px;
  background:linear-gradient(160deg,#3a2e08,#1c1404);
  transform-style:preserve-3d; transition:transform .12s ease-out; touch-action:none;
  box-shadow:0 12px 28px rgba(0,0,0,.5); padding:8px; box-sizing:border-box;
  display:flex; flex-direction:column; gap:5px; aspect-ratio:0.685; width:100%; }
.cosc-pwrap { padding:3px; border-radius:14px;
  background:conic-gradient(from var(--pa,0deg), #f0c75e, #ef7d57, #6ea8f7, #b07df0, #6ee7c8, #f0c75e);
  animation:cosc-spin 11s linear infinite; }
@keyframes cosc-spin { to { --pa:360deg; } }
.cosc .plate { position:relative; z-index:12; border-radius:5px; padding:5px 8px;
  display:flex; align-items:center; justify-content:space-between; font-size:11.5px; font-weight:800; letter-spacing:-0.02em; }
.cosc .gem { width:12px; height:12px; border-radius:50%; flex-shrink:0; }
.cosc .artbox { position:relative; z-index:2; flex:1; border-radius:5px; overflow:hidden;
  display:flex; align-items:center; justify-content:center; }
.cosc .artwrap { position:relative; z-index:2; filter:drop-shadow(0 3px 8px rgba(0,0,0,.3)) drop-shadow(0 0 12px rgba(255,255,255,.55)); }
.cosc .artbg { position:absolute; inset:0; z-index:1; }
.cosc .flavor { position:relative; z-index:12; border-radius:5px; padding:5px 8px;
  font-size:9px; line-height:1.5; min-height:30px; font-weight:600; }
.cosc .fx { position:absolute; inset:0; pointer-events:none; }
.cosc .fade-c { -webkit-mask-image:radial-gradient(ellipse 46% 36% at 50% 46%, rgba(0,0,0,.30) 0%, rgba(0,0,0,.30) 55%, #000 100%);
  mask-image:radial-gradient(ellipse 46% 36% at 50% 46%, rgba(0,0,0,.30) 0%, rgba(0,0,0,.30) 55%, #000 100%); }

/* 공통 빛 레이어 */
.cosc .sheen { mix-blend-mode:color-dodge; opacity:.45; z-index:4;
  background:repeating-linear-gradient(115deg, rgba(255,80,80,.6) 0%, rgba(255,200,60,.6) 6%, rgba(120,255,120,.6) 12%, rgba(80,200,255,.6) 18%, rgba(160,100,255,.6) 24%, rgba(255,80,80,.6) 30%);
  background-size:320% 320%; background-position:var(--hx,50%) var(--hy,50%); filter:saturate(1.25) brightness(.85); }
.cosc.idle .sheen, .cosc.idle .artbg { animation:cosc-flow 9s linear infinite; }
.cosc.idle .artbg { animation-duration:13s; }
@keyframes cosc-flow { 0% { background-position:0% 50%; } 50% { background-position:300% 50%; } 100% { background-position:0% 50%; } }
.cosc .glitter { mix-blend-mode:overlay; opacity:.55; z-index:5;
  background-image:radial-gradient(circle at 22% 30%, rgba(255,255,255,.95) .6px, transparent 1.5px),
    radial-gradient(circle at 64% 12%, rgba(255,255,255,.85) .5px, transparent 1.2px),
    radial-gradient(circle at 80% 58%, rgba(255,255,255,.95) .7px, transparent 1.6px),
    radial-gradient(circle at 38% 74%, rgba(255,255,255,.85) .5px, transparent 1.2px),
    radial-gradient(circle at 12% 88%, rgba(255,255,255,.95) .6px, transparent 1.4px);
  background-size:84px 84px; background-position:calc(var(--hx,50%) * -.4) calc(var(--hy,50%) * -.4); }
.cosc .glare { mix-blend-mode:soft-light; opacity:.85; z-index:6;
  background:radial-gradient(circle at var(--gx,50%) var(--gy,50%), rgba(255,255,255,.95) 0%, transparent 56%); }
.cosc .sweep { z-index:7; mix-blend-mode:screen; opacity:0;
  background:linear-gradient(115deg, transparent 38%, rgba(255,255,255,.75) 49%, rgba(190,230,255,.9) 50%, rgba(255,255,255,.75) 51%, transparent 62%);
  background-size:260% 260%; animation:cosc-sweep 5.2s ease-in-out infinite; }
@keyframes cosc-sweep { 0%,64% { opacity:0; background-position:120% 120%; } 72% { opacity:.9; } 88%,100% { opacity:0; background-position:-30% -30%; } }
.cosc .texlight { z-index:10; mix-blend-mode:screen; opacity:1;
  background:radial-gradient(circle at var(--gx,50%) var(--gy,50%), rgba(255,255,255,.85) 0%, transparent 30%);
  -webkit-mask-image:repeating-linear-gradient(45deg, #000 0 1.2px, transparent 1.2px 11px);
  mask-image:repeating-linear-gradient(45deg, #000 0 1.2px, transparent 1.2px 11px); }
.cosc .texture { z-index:9; mix-blend-mode:hard-light; opacity:.5; }

/* ── N (일반) — 중립 프레임 ── */
.cosc.g-n { background:linear-gradient(160deg,#4a4438,#2a2620); }
.cosc.g-n .plate, .cosc.g-n .flavor { background:rgba(255,255,255,.92); color:#4a4438; }
.cosc.g-n .gem { background:#8a8276; }
.cosc.g-n .artbg { background:radial-gradient(circle at 50% 42%, #efe9dd 0%, #d8d0c0 70%); }

/* ── R (레어) — 파랑 프레임 ── */
.cosc.g-r { background:linear-gradient(160deg,#243a66,#16244a); }
.cosc.g-r .plate, .cosc.g-r .flavor { background:rgba(255,255,255,.92); color:#1c2c52; }
.cosc.g-r .gem { background:#1F4FBF; }
.cosc.g-r .artbg { background:radial-gradient(circle at 50% 42%, #dfe8fa 0%, #b9c8ea 70%); }

/* ── E (에픽) — 다운그레이드 홀로 (§16-2) ── */
.cosc.g-e { background:linear-gradient(160deg,#5a4716,#2e230a); }
.cosc.g-e .plate, .cosc.g-e .flavor { background:rgba(255,251,238,.94); color:#4a3508; }
.cosc.g-e .gem { background:radial-gradient(circle at 35% 35%, #ffe9a8, #B8862A); box-shadow:0 0 6px rgba(255,220,120,.8); }
.cosc.g-e .artbg { background:
    repeating-linear-gradient(63deg, rgba(255,255,255,.07) 0 1px, transparent 1px 4px),
    linear-gradient(115deg, #f2e3c0, #ecd6dc 35%, #cfe2ec 70%, #f2e3c0);
  background-size:auto, 300% 300%; background-position:0 0, var(--hx,50%) var(--hy,50%); }
.cosc.g-e .sheen { opacity:.26; filter:saturate(.85) brightness(.8); }
.cosc.g-e .glitter { opacity:.35; }

/* ── L 공통 — 명판/플레이버 홀로 포일 ── */
.cosc.g-l .plate, .cosc.g-l .flavor {
  background:linear-gradient(100deg,#fdf6dd,#f3e3f5 40%,#ddeefa 75%,#fdf6dd); color:#4a3508;
  background-size:250% 100%; background-position:var(--hx,50%) 0; }
.cosc.g-l.idle .plate, .cosc.g-l.idle .flavor { animation:cosc-flow 11s linear infinite; }
.cosc.g-l .gem { background:conic-gradient(#f0c75e,#ef7d57,#6ea8f7,#b07df0,#f0c75e); box-shadow:0 0 7px rgba(255,255,255,.7); }
.cosc.g-l .prism { z-index:4; mix-blend-mode:color-dodge; opacity:.5; filter:saturate(1.35) brightness(.85) blur(1px);
  background:conic-gradient(from calc(var(--pa,0deg) * 1.5) at var(--gx,50%) var(--gy,50%),
    rgba(255,60,60,.7), rgba(255,220,60,.7), rgba(80,255,140,.7), rgba(60,200,255,.7), rgba(180,90,255,.7), rgba(255,60,60,.7)); }

/* V1 다이아 컷 — 기본 L (시드: --sa 각도 · --sh 색위상 · --sz 패싯) */
.cosc.v1 .artbg { background:
    repeating-conic-gradient(rgba(255,255,255,.14) 0 4deg, transparent 4deg 12deg),
    repeating-linear-gradient(45deg, rgba(255,255,255,.10) 0 2px, transparent 2px 7px),
    repeating-linear-gradient(-45deg, rgba(255,255,255,.10) 0 2px, transparent 2px 7px),
    conic-gradient(from var(--sa,0deg) at 50% 45%, #f7d98c, #f2a3b8 18%, #9fd4f0 38%, #c2a8f2 58%, #9ff0d2 78%, #f7d98c);
  filter:hue-rotate(var(--sh,0deg)); }
.cosc.v1 .texture { background-image:
    repeating-conic-gradient(from var(--sa,0deg), rgba(255,255,255,.38) 0deg 18deg, rgba(120,120,120,.18) 18deg 45deg, rgba(0,0,0,.30) 45deg 72deg, rgba(255,255,255,.20) 72deg 90deg),
    repeating-linear-gradient(45deg, rgba(255,255,255,.55) 0 1px, transparent 1px 11px, rgba(0,0,0,.45) 11px 12px, transparent 12px 22px),
    repeating-linear-gradient(-45deg, rgba(255,255,255,.40) 0 1px, transparent 1px 11px, rgba(0,0,0,.35) 11px 12px, transparent 12px 22px);
  background-size:var(--sz,12px) var(--sz,12px), auto, auto;
  background-position:calc(var(--gx,50%) * .25) calc(var(--gy,50%) * .25), calc(var(--gx,50%) * .5) calc(var(--gy,50%) * .5), calc(var(--gx,50%) * -.5) calc(var(--gy,50%) * -.5); }

/* V3 갤럭시 — 시즌 한정 */
.cosc.v3 { background:linear-gradient(160deg,#141029,#070512); }
.cosc.v3 .artbg { background:
    radial-gradient(circle at 24% 28%, rgba(190,120,255,.55) 0%, transparent 34%),
    radial-gradient(circle at 76% 64%, rgba(80,160,255,.5) 0%, transparent 38%),
    radial-gradient(circle at 58% 18%, rgba(255,140,200,.4) 0%, transparent 28%),
    linear-gradient(160deg,#1d1640,#0c0a24);
  filter:hue-rotate(var(--sh,0deg)); }
.cosc.v3 .texture { mix-blend-mode:screen; opacity:.8; background-image:
    radial-gradient(circle at 18% 22%, #fff .7px, transparent 1.6px),
    radial-gradient(circle at 72% 14%, rgba(255,255,255,.9) .5px, transparent 1.3px),
    radial-gradient(circle at 86% 52%, #fff .8px, transparent 1.8px),
    radial-gradient(circle at 42% 64%, rgba(255,255,255,.85) .5px, transparent 1.2px),
    radial-gradient(circle at 12% 84%, #fff .6px, transparent 1.5px);
  background-size:70px 70px; background-position:calc(var(--gx,50%) * .5) calc(var(--gy,50%) * .5); }
.cosc.v3 .sheen { opacity:.35; filter:saturate(1.1) brightness(.8) hue-rotate(40deg); }

/* V6 풀포일 메탈 — +10 승격 */
.cosc.v6 .artbg { background:linear-gradient(115deg, #e8e2d4, #d8d2e4 40%, #d0dee6 70%, #e8e2d4); filter:hue-rotate(var(--sh,0deg)); }
.cosc.v6 .texture { opacity:.25; background-image:
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.5' numOctaves='2'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='0.7'/%3E%3C/svg%3E"); }
.cosc.v6 .sheen { opacity:.7; filter:saturate(1.5) brightness(.95); }
.cosc.v6 .sheen2 { mix-blend-mode:color-dodge; opacity:.4; z-index:5;
  background:repeating-linear-gradient(245deg, rgba(255,120,80,.5) 0%, rgba(255,230,90,.5) 8%, rgba(90,255,160,.5) 16%, rgba(90,180,255,.5) 24%, rgba(200,100,255,.5) 32%, rgba(255,120,80,.5) 40%);
  background-size:320% 320%; background-position:calc(100% - var(--hx,50%)) var(--hy,50%); }
.cosc.idle.v6 .sheen2 { animation:cosc-flow 7s linear infinite reverse; }

/* ── 시그니처 파티클: motion(mo-*) × shape(sh-*) 직교 분해 (cardFxEngine kind 확장 설계 §2) ──
   z8 = 명판·플레이버 z12 아래(§16-2 가독성 3원칙 — 텍스트 불침범)
   신규 셰이프 CSS는 2호 캐릭터 튜너 확정값과 함께 추가 — 현재 실장 = fall × petal(록이).
   ⚠ 신규 셰이프에 filter:blur 금지(예산 maxBlurPx 1은 prism 전용) — 윤곽은 drop-shadow·그라디언트만. */
.cosc .sigfx { position:absolute; inset:0; z-index:8; pointer-events:none; overflow:hidden; }
.cosc .sigp { position:absolute; opacity:0; left:var(--px,50%); }
/* 모션: fall — 상단 시작·낙하 (구 cosc-petalfall, 키프레임 수치 동일) */
.cosc .mo-fall { top:-12%;
  animation:cosc-fx-fall calc(var(--dur,7s) / var(--speed,1)) linear infinite;
  animation-delay:var(--delay,0s); }
/* 셰이프: petal — 잎 모양 + 벚꽃 그라디언트 + 흰 윤곽 (록이 확정값) */
.cosc .sh-petal {
  width:calc(18px * var(--ps,1)); height:calc(22px * var(--ps,1));
  border-radius:100% 0 100% 0 / 100% 0 100% 0;
  background:linear-gradient(150deg, var(--pc1,#ff9ec4) 0%, var(--pc2,#ff6fa8) 100%);
  filter:drop-shadow(0 0 1.5px rgba(255,255,255,1)) drop-shadow(0 1px 2px rgba(120,40,80,.4)); }
/* ⚠ translate % = 꽃잎 자기 크기 기준이라 제자리 버그 → px 고정(카드 높이 관통, 목업 검증값) */
@keyframes cosc-fx-fall {
  0%   { transform:translate(0,0) rotate(0deg) rotateY(0deg); opacity:0; }
  6%   { opacity:var(--opamax,.92); }
  25%  { transform:translate(calc(26px * var(--sway,1)),105px) rotate(95deg) rotateY(160deg); }
  50%  { transform:translate(calc(-22px * var(--sway,1)),210px) rotate(190deg) rotateY(320deg); opacity:var(--opamax,.92); }
  75%  { transform:translate(calc(24px * var(--sway,1)),315px) rotate(280deg) rotateY(480deg); }
  96%  { opacity:calc(var(--opamax,.92) * 0.92); }
  100% { transform:translate(calc(-14px * var(--sway,1)),420px) rotate(360deg) rotateY(640deg); opacity:0; }
}

/* ── reduced-motion 가드 — 전 애니 비활성·장식 전용 레이어 숨김 (gachaFx·fmFx 동일 정책) ── */
@media (prefers-reduced-motion: reduce) {
  .cosc, .cosc *, .cosc-pwrap { animation:none !important; transition:none !important; }
  .cosc .sweep, .cosc .sigfx { display:none !important; }
}
`;

let cssInjected = false;
function injectCss() {
  if (cssInjected || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.dataset.cosmeticCard = '1';
  el.textContent = CSS;
  document.head.appendChild(el);
  cssInjected = true;
}

/**
 * 코스메틱 카드 — grade·level·시즌 여부로 이펙트 티어 자동 결정.
 * @param {{slug:string, grade:'N'|'R'|'E'|'L', level?:number, seedId?:string, season?:boolean,
 *          width?:number, idle?:boolean, tilt?:boolean, flavor?:string, onClick?:Function}} props
 */
export function CosmeticCard({ slug, grade, level = 0, seedId, season = false, width = 200, idle = true, tilt = true, flavor, onClick }) {
  injectCss();
  const ref = useRef(null);
  // 이펙트 스펙 = 순수 엔진 산출(결정론: 같은 seedId = 같은 결·같은 벚꽃 배치)
  const fx = resolveCardFx({ grade, slug, level, season, seedId });
  const { seed, variant } = fx;
  const isL = fx.prismBorder;
  const idleOn = idle && fx.idleEligible; // N/R = idle 애니 차단(무의미 리페인트 제거, 시각 무변경)
  const ko = CAST[slug]?.ko || slug;
  const role = (CAST[slug]?.role || '').split('·')[0].trim();

  const onMove = useCallback((e) => {
    if (!tilt || !ref.current) return;
    const card = ref.current;
    const r = card.getBoundingClientRect();
    const nx = ((e.clientX - r.left) / r.width) * 2 - 1;
    const ny = ((e.clientY - r.top) / r.height) * 2 - 1;
    card.classList.remove('idle');
    card.style.transform = `perspective(700px) rotateY(${nx * 13}deg) rotateX(${-ny * 13}deg)`;
    card.style.setProperty('--hx', `${50 + nx * 50}%`);
    card.style.setProperty('--hy', `${50 + ny * 50}%`);
    card.style.setProperty('--gx', `${50 + nx * 42}%`);
    card.style.setProperty('--gy', `${50 + ny * 42}%`);
  }, [tilt]);
  const onLeave = useCallback(() => {
    const card = ref.current;
    if (!card) return;
    card.style.transform = '';
    ['--hx', '--hy', '--gx', '--gy'].forEach((v) => card.style.removeProperty(v));
    if (idleOn) card.classList.add('idle');
  }, [idleOn]);

  const body = (
    <div ref={ref} onPointerMove={onMove} onPointerLeave={onLeave} onClick={onClick}
      className={`cosc g-${grade.toLowerCase()} ${variant} ${idleOn ? 'idle' : ''}`}
      style={{ '--sa': `${seed.angle}deg`, '--sh': `${seed.hue}deg`, '--sz': `${seed.facet}px`, cursor: onClick ? 'pointer' : 'default' }}>
      <div className="plate">
        <span>{ko} {role && <small style={{ fontWeight: 600, opacity: 0.65 }}>{role}</small>}</span>
        <span className="gem" />
      </div>
      <div className="artbox">
        <div className="artbg fx" />
        <div className="artwrap"><CastAvatar id={slug} size={width * 0.62} /></div>
      </div>
      {/* 강화 수치 꼬리(" · 강화 +N") 제거 — 강화는 2026-08-26부터 꺼져 있어(enhance_enabled=false) 없는 기능을
          말하게 된다. 보류 전에 강화한 카드는 level>0이라 실제로 찍혔다. 강화를 다시 켤 때 이 줄도 함께 되돌린다. */}
      {/* 등급을 사람 말로 — 프로필 시트 배지("일반 카드")와 한 화면에서 "N 등급"이 섞이던 것을 맞춘다(2026-09-12) */}
      <div className="flavor">{flavor || `${ko}의 ${GRADE_META[grade]?.label || grade} 카드`}</div>
      {/* 이펙트 레이어 — 등급별 (가독성: 중앙 마스크 fade-c) */}
      {isL && <div className="fx prism fade-c" />}
      {isL && <div className="fx texture fade-c" />}
      {(isL || grade === 'E') && <div className="fx glitter" />}
      {(isL || grade === 'E') && <div className="fx sheen fade-c" />}
      {isL && variant === 'v6' && <div className="fx sheen2 fade-c" />}
      {(isL || grade === 'E') && <div className="fx glare" />}
      {isL && <div className="fx texlight" />}
      {isL && <div className="fx sweep" />}
      {/* 시그니처 파티클 — 엔진 결정론 산출(같은 seedId=같은 배치). idle 아닐 땐 미장착.
          셰이프·모션은 시그니처당 균일 — 엔진 스펙을 1회 읽어 클래스에 반영(설계 §2) */}
      {idleOn && fx.particles.length > 0 && fx.signature && (
        <div className="sigfx" aria-hidden="true">
          {fx.particles.map((p, i) => (
            <i key={i} className={`sigp sh-${fx.signature.shape} mo-${fx.signature.motion}`} style={{
              '--px': `${p.xPct}%`, '--ps': p.scale,
              '--dur': `${p.durS}s`, '--delay': `${p.delayS}s`,
              '--pc1': p.c1, '--pc2': p.c2,
            }} />
          ))}
        </div>
      )}
    </div>
  );

  // L = 회전 프리즘 테두리 래퍼
  if (isL) return <div className="cosc-pwrap" style={{ width }}>{body}</div>;
  return <div style={{ width }}>{body}</div>;
}
